import {
  lstatSync,
  mkdirSync,
  mkdtempSync,
  readFileSync,
  readlinkSync,
  readdirSync,
  renameSync,
  rmSync,
  writeFileSync,
} from "node:fs";
import { dirname, isAbsolute, join, sep } from "node:path";

export interface ManagedPluginTreeFile {
  bytes: Buffer;
  path: string;
}

interface ManagedTreeSnapshot {
  directories: Set<string>;
  files: Map<string, Buffer>;
  symlinks: Map<string, string>;
}

function lstatIfExists(path: string): ReturnType<typeof lstatSync> | null {
  try {
    return lstatSync(path);
  } catch (error) {
    const code = typeof error === "object" && error && "code" in error
      ? String(error.code)
      : undefined;
    if (code === "ENOENT" || code === "ENOTDIR") return null;
    throw error;
  }
}

function assertSafeRelativePath(path: string): void {
  const segments = path.split(/[\\/]/);
  const hasUnsafeSegment = segments.some(
    (segment) => !segment || segment === "." || segment === "..",
  );
  if (isAbsolute(path) || hasUnsafeSegment) {
    throw new Error(`Refusing to sync managed plugin tree: unsafe destination path '${path}'.`);
  }
}

function occupiesExpectedManagedPath(
  relativePath: string,
  expectedPaths: ReadonlySet<string>,
): boolean {
  const descendantPrefix = `${relativePath}${sep}`;
  return [...expectedPaths].some(
    (expectedPath) => expectedPath === relativePath || expectedPath.startsWith(descendantPrefix),
  );
}

function readManagedTreeSnapshot(
  root: string,
  expectedPaths: ReadonlySet<string>,
): ManagedTreeSnapshot | null {
  const rootStat = lstatIfExists(root);
  if (!rootStat) return null;
  if (rootStat.isSymbolicLink()) {
    throw new Error("Refusing to sync managed plugin tree: destination root is a symbolic link.");
  }
  if (!rootStat.isDirectory()) {
    throw new Error("Refusing to sync managed plugin tree: destination root is not a directory.");
  }

  const snapshot: ManagedTreeSnapshot = {
    directories: new Set<string>(),
    files: new Map<string, Buffer>(),
    symlinks: new Map<string, string>(),
  };

  const visit = (directory: string, relativeDirectory: string): void => {
    for (const name of readdirSync(directory)) {
      const path = join(directory, name);
      const relativePath = relativeDirectory ? join(relativeDirectory, name) : name;
      const entryStat = lstatSync(path);
      if (entryStat.isSymbolicLink()) {
        if (occupiesExpectedManagedPath(relativePath, expectedPaths)) {
          throw new Error(
            `Refusing to sync managed plugin tree: destination entry '${relativePath}' is a symbolic link.`,
          );
        }
        // Preserve the link itself in the concurrency snapshot without reading
        // through it. Unexpected legacy entries are discarded by the later
        // same-parent directory swap.
        snapshot.symlinks.set(relativePath, readlinkSync(path));
        continue;
      }
      if (entryStat.isDirectory()) {
        snapshot.directories.add(relativePath);
        visit(path, relativePath);
        continue;
      }
      if (entryStat.isFile()) {
        snapshot.files.set(relativePath, readFileSync(path));
        continue;
      }
      throw new Error(
        `Refusing to sync managed plugin tree: destination entry '${relativePath}' is not a regular file or directory.`,
      );
    }
  };

  visit(root, "");
  return snapshot;
}

function setsEqual(left: ReadonlySet<string>, right: ReadonlySet<string>): boolean {
  return left.size === right.size && [...left].every((value) => right.has(value));
}

function snapshotsEqual(
  left: ManagedTreeSnapshot | null,
  right: ManagedTreeSnapshot | null,
): boolean {
  if (!left || !right) return left === right;
  if (
    !setsEqual(left.directories, right.directories) ||
    left.files.size !== right.files.size ||
    left.symlinks.size !== right.symlinks.size
  ) {
    return false;
  }
  const filesEqual = [...left.files].every(([path, bytes]) => {
    const other = right.files.get(path);
    return other !== undefined && Buffer.compare(bytes, other) === 0;
  });
  if (!filesEqual) return false;
  return [...left.symlinks].every(
    ([path, target]) => right.symlinks.get(path) === target,
  );
}

function expectedDirectories(files: readonly ManagedPluginTreeFile[]): Set<string> {
  const directories = new Set<string>();
  for (const file of files) {
    let current = dirname(file.path);
    while (current !== "." && current !== "/") {
      directories.add(current);
      const parent = dirname(current);
      if (parent === current) break;
      current = parent;
    }
  }
  return directories;
}

function snapshotMatchesFiles(
  snapshot: ManagedTreeSnapshot | null,
  files: readonly ManagedPluginTreeFile[],
): boolean {
  if (!snapshot || snapshot.files.size !== files.length) return false;
  if (snapshot.symlinks.size > 0) return false;
  if (!setsEqual(snapshot.directories, expectedDirectories(files))) return false;
  return files.every((file) => {
    const existing = snapshot.files.get(file.path);
    return existing !== undefined && Buffer.compare(existing, file.bytes) === 0;
  });
}

function stageAndReplaceManagedTree(
  destinationRoot: string,
  files: readonly ManagedPluginTreeFile[],
  originalSnapshot: ManagedTreeSnapshot | null,
  expectedPaths: ReadonlySet<string>,
): void {
  const destinationParent = dirname(destinationRoot);
  mkdirSync(destinationParent, { recursive: true });
  const transactionRoot = mkdtempSync(join(destinationParent, ".orgx-plugin-sync-"));
  const nextRoot = join(transactionRoot, "next");
  const previousRoot = join(transactionRoot, "previous");
  let preserveTransaction = false;

  try {
    mkdirSync(nextRoot);
    for (const file of files) {
      const destination = join(nextRoot, file.path);
      mkdirSync(dirname(destination), { recursive: true });
      writeFileSync(destination, file.bytes);
    }

    const latestSnapshot = readManagedTreeSnapshot(destinationRoot, expectedPaths);
    if (!snapshotsEqual(originalSnapshot, latestSnapshot)) {
      throw new Error("Refusing to sync managed plugin tree: destination changed during staging.");
    }

    let previousMoved = false;
    try {
      if (latestSnapshot) {
        renameSync(destinationRoot, previousRoot);
        previousMoved = true;
      }
      renameSync(nextRoot, destinationRoot);
      if (previousMoved) {
        rmSync(previousRoot, { force: true, recursive: true });
        previousMoved = false;
      }
    } catch (error) {
      if (previousMoved && !lstatIfExists(destinationRoot)) {
        try {
          renameSync(previousRoot, destinationRoot);
          previousMoved = false;
        } catch (rollbackError) {
          preserveTransaction = true;
          throw new AggregateError(
            [error, rollbackError],
            `Managed plugin replacement failed and rollback could not restore the prior tree. Recovery data remains at ${transactionRoot}.`,
          );
        }
      }
      throw error;
    }
  } finally {
    if (!preserveTransaction) {
      rmSync(transactionRoot, { force: true, recursive: true });
    }
  }
}

export function replaceManagedPluginTreeIfChanged(
  destinationRoot: string,
  files: readonly ManagedPluginTreeFile[],
): boolean {
  const paths = new Set<string>();
  for (const file of files) {
    assertSafeRelativePath(file.path);
    if (paths.has(file.path)) {
      throw new Error(`Refusing to sync managed plugin tree: duplicate destination path '${file.path}'.`);
    }
    paths.add(file.path);
  }

  const snapshot = readManagedTreeSnapshot(destinationRoot, paths);
  if (snapshotMatchesFiles(snapshot, files)) return false;
  stageAndReplaceManagedTree(destinationRoot, files, snapshot, paths);
  return true;
}
